import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { formatDateTime } from '@/format';
import { confirmAction } from '@/stores/confirm';
import { toast } from '@/stores/toast';
import { describeDevice } from './device';

/**
 * Where this account is signed in. A lost phone or a PC someone forgot to
 * sign out of can be ended from here, without changing the password.
 */
export function Sessions() {
  const queryClient = useQueryClient();
  const sessions = useQuery({
    queryKey: ['settings', 'sessions'],
    queryFn: () => api.get('/api/v1/settings/sessions'),
  });

  const end = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/settings/sessions/${id}`),
    onSuccess: () => {
      toast.success('That device is signed out.');
      void queryClient.invalidateQueries({ queryKey: ['settings', 'sessions'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not sign that device out.'),
  });

  const endOthers = useMutation({
    mutationFn: () => api.post<{ revoked: number }>('/api/v1/settings/sessions/revoke-others'),
    onSuccess: (res) => {
      toast.success(
        res.revoked === 0
          ? 'No other devices were signed in.'
          : `${res.revoked} other ${res.revoked === 1 ? 'device is' : 'devices are'} signed out.`,
      );
      void queryClient.invalidateQueries({ queryKey: ['settings', 'sessions'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not sign the other devices out.'),
  });

  const list = sessions.data ?? [];
  const others = list.filter((s) => !s.current).length;

  return (
    <div className="settingsapp__block">
      <h3>Where you are signed in</h3>
      <p className="settings__hint">
        If you see a device you do not know, sign it out and change your password.
      </p>

      {sessions.isPending && <p className="muted">Loading…</p>}
      {sessions.isError && (
        <p className="errornote" role="alert">
          <span aria-hidden="true">⚠ </span>
          Could not load the list. {sessions.error instanceof Error ? sessions.error.message : ''}
        </p>
      )}

      {list.length > 0 && (
        <div className="tablewrap">
          <table className="table">
            <thead>
              <tr>
                <th>Device</th>
                <th>Network address</th>
                <th>Signed in</th>
                <th>Last used</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {list.map((s) => (
                <tr key={s.id}>
                  <td>
                    {describeDevice(s.userAgent)}
                    {s.current && <span className="pill pill--ok sessions__here">This computer</span>}
                  </td>
                  <td className="mono mono--ascase">{s.ipAddress ?? '—'}</td>
                  <td>{formatDateTime(s.createdAt)}</td>
                  <td>{formatDateTime(s.lastUsedAt)}</td>
                  <td className="num">
                    {!s.current && (
                      <button
                        type="button"
                        className="btn btn--sm"
                        disabled={end.isPending}
                        onClick={() =>
                          void confirmAction({
                            title: 'Sign out that device?',
                            message: `${describeDevice(s.userAgent)} will be signed out straight away.`,
                            confirmLabel: 'Sign it out',
                          }).then((ok) => ok && end.mutate(s.id))
                        }
                      >
                        Sign out
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="settingsapp__blockrow">
        <button
          type="button"
          className="btn btn--sm"
          disabled={others === 0 || endOthers.isPending}
          title={others === 0 ? 'You are not signed in anywhere else' : undefined}
          onClick={() =>
            void confirmAction({
              title: 'Sign out everywhere else?',
              message: 'Every other computer and phone signed in as you will be signed out. You stay signed in here.',
              confirmLabel: 'Sign out everywhere else',
            }).then((ok) => ok && endOthers.mutate())
          }
        >
          Sign out everywhere else
        </button>
      </div>
    </div>
  );
}
